/**
 * "Talk to sales" — the dialog behind every contact CTA on the public site.
 *
 * It is deliberately NOT the registration form. Someone opening this has not
 * picked a plan; they want to know whether the platform fits a club of 400
 * across three cities before they commit to a price. Sending them to
 * /register-portal would make them choose one first, which is the thing they
 * came to ask about.
 *
 * So it asks the two questions that let the platform owner answer well — how
 * big is the club, and what do they actually need — and files the answer in
 * the Super Admin console next to the portal requests. There is no account and
 * no sign-in involved at any point.
 */

import { useEffect, useRef, useState } from 'react';
import { platform } from '../lib/api';
import { useBackToClose } from '../hooks/useBackToClose';
import { Field, FormError, PhoneInput, Row2, TEXTAREA_CLS } from './ui';

export function SalesDialog({ onClose }) {
  useBackToClose(true, onClose);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    orgName: '',
    memberCount: '',
    message: '',
  });
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    firstRef.current?.focus();
    // The page behind must not scroll under the dialog on a phone.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      setSent(
        await platform.salesEnquiry({
          ...form,
          memberCount: form.memberCount === '' ? null : Number(form.memberCount),
        }),
      );
    } catch (err) {
      setError(err?.message ?? 'Could not send that. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const contact = sent?.contact ?? {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-800/70 px-4 py-6 backdrop-blur-[2px] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Talk to sales"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg border border-line bg-white">
        <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
          <div>
            <p className="font-market text-[10px] font-bold uppercase tracking-[0.2em] text-sea-600">
              RunnerCrew
            </p>
            <h2 className="mt-1 font-market text-[22px] font-extrabold tracking-[-0.03em] text-ink-700">
              {sent ? 'Thanks — we have it' : 'Talk to sales'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-1 px-2 py-1 font-market text-xl leading-none text-copy-label transition-colors hover:text-ink-700"
          >
            ×
          </button>
        </div>

        {sent ? (
          <div className="px-6 py-6">
            <p className="text-sm leading-relaxed text-copy-muted">
              Our team will contact you{' '}
              <strong className="text-ink-700">within 2 working hours</strong> on
              the number you gave us. Nothing has been charged and no account has
              been created.
            </p>
            <div className="mt-4 border border-line bg-ground px-4 py-3">
              <span className="font-market text-[10px] uppercase tracking-[0.18em] text-copy-label">
                Reference
              </span>
              <span className="ml-2.5 font-market text-[15px] font-extrabold tracking-[0.08em] text-ink-700">
                {sent.reference}
              </span>
            </div>
            {(contact.email || contact.phone) && (
              <div className="mt-4">
                <p className="font-market text-[10px] font-bold uppercase tracking-[0.18em] text-copy-label">
                  Or reach us first
                </p>
                <p className="mt-1.5 font-market text-[15px] font-bold text-ink-700">
                  {contact.name || 'RunnerCrew'}
                </p>
                {contact.email && (
                  <a className="block text-sm text-gold-800 underline" href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                )}
                {contact.phone && (
                  <a className="block font-market text-sm tabular-nums text-ink-700" href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full bg-gold-500 py-3 font-market text-sm font-semibold uppercase tracking-wide text-ink-800"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5">
            <p className="mb-4 text-sm leading-relaxed text-copy-muted">
              Tell us about your club and we will call you back — pricing for a
              large roster, moving from another system, anything you need to know
              before you sign up.
            </p>

            <Row2>
              <Field label="Your name" required>
                <input ref={firstRef} value={form.name} onChange={set('name')} />
              </Field>
              <Field label="Mobile number" required>
                <PhoneInput
                  value={form.phone}
                  onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                />
              </Field>
            </Row2>

            <Field label="Email address" required>
              <input type="email" value={form.email} onChange={set('email')} placeholder="you@club.com" />
            </Field>

            <Row2>
              <Field label="Club / organisation" required>
                <input
                  value={form.orgName}
                  onChange={set('orgName')}
                  placeholder="e.g. Coimbatore Track Club"
                />
              </Field>
              <Field label="How many members">
                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={form.memberCount}
                  onChange={set('memberCount')}
                  placeholder="e.g. 120"
                />
              </Field>
            </Row2>

            <Field label="What do you need?">
              <textarea
                className={TEXTAREA_CLS}
                value={form.message}
                onChange={set('message')}
                placeholder="How many clubs, where you run, anything you want us to cover on the call…"
              />
            </Field>

            <FormError>{error}</FormError>

            <button
              type="submit"
              disabled={busy}
              className="mt-2 w-full bg-gold-500 py-3 font-market text-sm font-semibold uppercase tracking-wide text-ink-800 disabled:opacity-60"
            >
              {busy ? 'Sending…' : 'Request a call back'}
            </button>
            <p className="mt-3 text-center text-xs text-copy-label">
              Ready to start?{' '}
              <a href="/register-portal" className="text-gold-800 underline">
                Create your portal
              </a>{' '}
              instead — no call needed.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
